import { useState } from 'react';
import { DepartmentOverduePanel } from '../components/tasks/DepartmentOverdue.tsx';
import { useCommissionTracker, type CommissionLine, type CommissionPage } from '../api/commissions.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import {
  Chip, DashboardFrame, Waiting, listState, useUrlFilters,
} from '../components/DashboardFrame.tsx';
import { ScrollToTop } from '../components/ScrollToTop.tsx';
import { CommissionLineView } from '../components/CommissionLines.tsx';

/**
 * The sorts offered. Longest waiting first by default: how long an order has gone uncommissioned is the only thing on
 * this screen that gets worse on its own.
 */
const SORTS: { value: string; label: string }[] = [
  { value: '-age', label: 'Longest waiting' },
  { value: 'age', label: 'Shortest waiting' },
  { value: 'state', label: 'State' },
  { value: 'name', label: 'Name' },
  { value: 'client_ref', label: 'Reference' },
  { value: 'county', label: 'County' },
];

/**
 * The commission tracker (167, P-24; Arthur's option A, D-126).
 *
 * **One line per ACTING order**, its steps read from the records, its state the first unmet step, in the database's
 * own words. "If Procura doesn't know it, it doesn't exist" (Q23, D-128): nobody sets a state here. A person records
 * the APPLICATION or opens a BLOCKER on the line; the oath, the bond and the commission are recorded on the order.
 *
 * **The figures are the caseload, not the filter**: "<X> clients commissioned" and "<Y> orders pending commission".
 * A client the tracker covers with no acting order is a red line, never a quiet "status unknown" (26).
 */
export function CommissionTracker() {
  return (
    <DashboardFrame
      dashboard="commission"
      title="Commissions"
      subtitle="Each acting order, and what its commission is waiting for, read from the records."
    >
      {/* A-#83 A5: every director's landing opens on their department's overdue work. */}
      <DepartmentOverduePanel />
      <CommissionBody />
    </DashboardFrame>
  );
}

function CommissionBody() {
  const [params, update] = useUrlFilters();
  const state = params.get('state') ?? undefined;
  const county = params.get('county') ?? undefined;
  const kinds = params.get('kinds') === 'show' ? 'show' : undefined;
  const sort = params.get('sort') ?? '-age';
  const [said, setSaid] = useState<string | null>(null);

  const filters = { state, county, kinds, sort } as const;
  const query = useCommissionTracker(filters);
  const { failure, takeover } = listState(query);
  const data = takeover ? undefined : query.data;
  const lines = data?.lines ?? [];
  const label = (s: string) => data?.states.find((x) => x.state === s)?.label ?? s;

  return (
    <>
      <div className="border-b border-neutral-200 px-4 py-3 lg:px-5">
        <Headline data={data} loading={!data && !takeover} />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Chip active={!state} onClick={() => update({ state: null })}>
            Every line{' '}
            <span className="font-normal">{(data?.by_state ?? []).reduce((n, s) => n + Number(s.lines), 0).toLocaleString()}</span>
          </Chip>
          {(data?.by_state ?? []).map((s) => (
            <Chip key={s.state} active={state === s.state} onClick={() => update({ state: state === s.state ? null : s.state })}>
              {label(s.state)} <span className="font-normal">{Number(s.lines).toLocaleString()}</span>
            </Chip>
          ))}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Hidden by default: a client whose appointment needs no commission is nobody's outstanding work. */}
          <Chip active={kinds === 'show'} onClick={() => update({ kinds: kinds === 'show' ? null : 'show' })}>
            Show clients who don’t need commissions{' '}
            <span className="font-normal">{(data?.not_in_the_race ?? 0).toLocaleString()}</span>
          </Chip>

          {(data?.counties.length ?? 0) > 0 && (
            <>
              <label htmlFor="commission-county" className="sr-only">County</label>
              <select
                id="commission-county"
                value={county ?? ''}
                onChange={(e) => update({ county: e.target.value || null })}
                className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem] text-neutral-800"
              >
                <option value="">Every county</option>
                {(data?.counties ?? []).map((c) => (
                  <option key={c.county_code} value={c.county_code}>
                    {c.county_code.replace(/_/g, ' ')} ({c.lines})
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="commission-sort" className="sr-only">Sort by</label>
          <select
            id="commission-sort"
            value={sort}
            onChange={(e) => update({ sort: e.target.value === '-age' ? null : e.target.value })}
            className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem] text-neutral-800 lg:ml-auto"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {said && <p role="status" className="mt-2 text-sm text-neutral-800">{said}</p>}
      </div>

      {!takeover && (
        <Staleness
          updatedAt={query.dataUpdatedAt}
          isFetching={query.isFetching}
          error={query.error}
          onRefresh={() => void query.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="The commission tracker"
          error={failure}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          forbiddenIsSession={false}
        />
      ) : query.isPending ? (
        <Waiting>Reading the orders…</Waiting>
      ) : query.isSuccess && !query.isPlaceholderData && lines.length === 0 ? (
        <Waiting>{state ? 'No line is in that state.' : 'No client you can see needs a commission.'}</Waiting>
      ) : (
        <ol className="divide-y divide-neutral-200 pb-20">
          {lines.map((line) => (
            <li key={`${line.client_id}:${line.court_order_id ?? 'none'}`} className="px-4 py-3 lg:px-5">
              <LineRow line={line} data={data} onSaid={setSaid} />
            </li>
          ))}
        </ol>
      )}

      <ScrollToTop />
    </>
  );
}

function LineRow({ line, data, onSaid }: { line: CommissionLine; data: CommissionPage | undefined; onSaid: (m: string) => void }) {
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_9rem] lg:gap-4">
      <CommissionLineView line={line} showClient mayRecord={data?.may_record ?? false} mayResolve={data?.may_resolve ?? false}
                          onSaid={onSaid} />
      <p className="mt-1 text-sm text-neutral-700 lg:mt-0 lg:text-right">
        {line.age_days != null && line.court_order_id && !line.commissioned
          ? <><span className="font-semibold tabular-nums text-neutral-900">{ageWords(line.age_days)}</span> since the order</>
          : null}
        {line.county_code && <span className="block text-neutral-600">{line.county_code.replace(/_/g, ' ')}</span>}
      </p>
    </div>
  );
}

function ageWords(days: number): string {
  return days < 60 ? `${days} days` : days < 730 ? `${Math.trunc(days / 30)} months` : `${Math.trunc(days / 365)} years`;
}

/**
 * "<X> clients commissioned" and "<Y> orders pending commission" (27), Y in the large type: it is the work. The red
 * lines are said apart, never folded into either.
 */
function Headline({ data, loading }: { data: CommissionPage | undefined; loading: boolean }) {
  if (!data) {
    return <p className="text-[0.9375rem] text-neutral-800">{loading ? 'Counting…' : 'Not loaded'}</p>;
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 tabular-nums">
      <p className="text-[0.9375rem] text-neutral-800">
        <span className="text-2xl font-bold text-neutral-900">{data.orders_pending.toLocaleString()}</span>{' '}
        orders pending commission
      </p>
      <p className="text-[0.9375rem] text-neutral-600">· {data.clients_commissioned.toLocaleString()} clients commissioned</p>
      {(data.blocked > 0 || data.no_acting_order > 0) && (
        <p className="w-full text-sm text-neutral-700">
          {data.blocked > 0 && <>{data.blocked.toLocaleString()} blocked</>}
          {data.blocked > 0 && data.no_acting_order > 0 && ' · '}
          {data.no_acting_order > 0 && (
            <span className="font-semibold text-critical-edge">
              {data.no_acting_order.toLocaleString()} with no acting order on file: the commission cannot be tracked
            </span>
          )}
        </p>
      )}
    </div>
  );
}
